/**
 * FAM Cookie Manager
 * Handles GDPR compliant cookie consent and script/iframe blocking.
 */

const CONSENT_KEY = 'fam_cookie_consent';

export const CookieManager = {
  init() {
    this.checkConsent();
    this.bindEvents();
  },

  getConsentData() {
    const data = localStorage.getItem(CONSENT_KEY);
    if (!data) return null;
    
    try {
      const parsed = JSON.parse(data);
      // Soporte retrocompatible para el formato booleano anterior
      if (typeof parsed.accepted === 'boolean') {
        return {
          necessary: true,
          marketing: parsed.accepted
        };
      }
      return parsed;
    } catch (e) {
      return null;
    }
  },

  checkConsent() {
    const consent = this.getConsentData();
    if (!consent) {
      this.showBanner();
      this.blockElements();
    } else {
      if (consent.marketing) {
        this.loadElements();
      } else {
        this.blockElements();
      }
    }
  },

  showBanner() {
    // Inject banner if not exists
    if (!document.getElementById('fam-cookie-banner')) {
      const bannerHTML = `
        <div id="fam-cookie-banner" class="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 transition-transform duration-700 translate-y-full">
          <div class="max-w-4xl mx-auto bg-white/80 backdrop-blur-xl border border-white/20 shadow-2xl p-6 md:p-8 rounded-2xl flex flex-col md:flex-row items-center gap-6">
            <div class="flex-1">
              <h3 class="font-heading text-xl font-bold text-tinta mb-2 flex items-center gap-2">
                <span class="w-2 h-2 bg-accent rounded-full animate-pulse"></span>
                Tu privacidad nos importa
              </h3>
              <p class="text-sm text-tinta/70 leading-relaxed">
                Utilizamos cookies propias y de terceros para mejorar tu experiencia, analizar el tráfico y mostrarte contenido personalizado. Puedes aceptarlas todas o configurar tus preferencias.
                <a href="politica-cookies.html" class="text-accent underline font-bold ml-1">Saber más</a>.
              </p>
            </div>
            <div class="flex flex-wrap gap-3 shrink-0">
              <button id="cookie-reject" class="px-6 py-3 border-2 border-tinta/10 text-tinta font-bold text-xs uppercase tracking-widest hover:bg-tinta hover:text-white transition-all rounded-lg">
                Rechazar
              </button>
              <button id="cookie-configure" class="px-6 py-3 border-2 border-accent/20 text-accent font-bold text-xs uppercase tracking-widest hover:bg-accent/10 transition-all rounded-lg">
                Configurar
              </button>
              <button id="cookie-accept" class="px-8 py-3 bg-accent text-white font-bold text-xs uppercase tracking-widest hover:bg-primary transition-all shadow-lg shadow-accent/20 rounded-lg">
                Aceptar todas
              </button>
            </div>
          </div>
        </div>
      `;
      document.body.insertAdjacentHTML('beforeend', bannerHTML);
      
      setTimeout(() => {
        const banner = document.getElementById('fam-cookie-banner');
        if(banner) banner.classList.remove('translate-y-full');
      }, 100);
    }
  },

  hideBanner() {
    const banner = document.getElementById('fam-cookie-banner');
    if (banner) {
      banner.classList.add('translate-y-full');
      setTimeout(() => banner.remove(), 700);
    }
  },

  showConfigModal() {
    if (document.getElementById('fam-cookie-modal')) {
      document.getElementById('fam-cookie-modal').classList.remove('hidden');
      return;
    }

    const consent = this.getConsentData();
    const marketingChecked = consent ? consent.marketing : false;

    const modalHTML = `
      <div id="fam-cookie-modal" class="fixed inset-0 z-[10000] flex items-center justify-center p-4">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-tinta/40 backdrop-blur-sm transition-opacity" id="cookie-modal-backdrop"></div>
        
        <!-- Modal Content -->
        <div class="relative bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in duration-300">
          <div class="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <h3 class="font-heading text-xl font-bold text-tinta">Configuración de Cookies</h3>
            <button id="cookie-modal-close" class="text-gray-400 hover:text-accent transition p-1">
              <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          
          <div class="p-6 overflow-y-auto flex-1 space-y-6">
            <p class="text-sm text-gray-600 leading-relaxed">
              Cuando visitas nuestro sitio web, podemos almacenar información en tu navegador mediante el uso de cookies. Esta información se usa para que el sitio funcione correctamente y para fines analíticos y de rendimiento. Puedes elegir qué categorías de cookies permitir.
            </p>
            
            <div class="space-y-4">
              <!-- Necesarias -->
              <div class="flex flex-col sm:flex-row items-start justify-between gap-4 p-5 rounded-xl bg-gray-50 border border-gray-200">
                <div class="flex-1">
                  <h4 class="font-bold text-tinta mb-1 text-base">Cookies Estrictamente Necesarias / Técnicas</h4>
                  <p class="text-sm text-gray-600">Estas cookies son esenciales para que el sitio web funcione con normalidad, garantizando opciones de seguridad, protección contra spam (reCAPTCHA) y prevención de fraude. No se pueden desactivar.</p>
                </div>
                <div class="shrink-0 flex items-center mt-1 sm:mt-0">
                  <span class="px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-xs font-bold uppercase tracking-widest">Siempre Activo</span>
                </div>
              </div>
              
              <!-- Publicitarias -->
              <div class="flex flex-col sm:flex-row items-start justify-between gap-4 p-5 rounded-xl border border-gray-200 hover:border-accent/30 transition-colors">
                <div class="flex-1">
                  <h4 class="font-bold text-tinta mb-1 text-base">Cookies de Terceros y Publicidad</h4>
                  <p class="text-sm text-gray-600">Nos permiten cargar contenidos externos (como mapas interactivos) y recopilar información sobre cómo usas el sitio web para mejorar tu experiencia (estadísticas, preferencias).</p>
                </div>
                <div class="shrink-0 mt-1 sm:mt-0 flex items-center">
                  <label class="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" id="toggle-marketing" class="sr-only peer" ${marketingChecked ? 'checked' : ''}>
                    <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
          
          <div class="p-6 border-t border-gray-100 flex flex-wrap gap-3 justify-end bg-gray-50">
            <button id="cookie-reject-all-modal" class="px-6 py-3 border-2 border-tinta/10 text-tinta font-bold text-xs uppercase tracking-widest hover:bg-tinta hover:text-white transition-all rounded-lg">
              Rechazar Todas
            </button>
            <button id="cookie-save-preferences" class="px-6 py-3 bg-accent text-white font-bold text-xs uppercase tracking-widest hover:bg-primary transition-all shadow-lg rounded-lg">
              Guardar Preferencias
            </button>
          </div>
        </div>
      </div>
    `;
    
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    const closeModal = () => {
      const modal = document.getElementById('fam-cookie-modal');
      if (modal) modal.classList.add('hidden');
    };

    document.getElementById('cookie-modal-close').addEventListener('click', closeModal);
    document.getElementById('cookie-modal-backdrop').addEventListener('click', closeModal);
    
    document.getElementById('cookie-reject-all-modal').addEventListener('click', () => {
      this.setConsent({ necessary: true, marketing: false });
      closeModal();
    });

    document.getElementById('cookie-save-preferences').addEventListener('click', () => {
      const isMarketingAccepted = document.getElementById('toggle-marketing').checked;
      this.setConsent({ necessary: true, marketing: isMarketingAccepted });
      closeModal();
    });
  },

  bindEvents() {
    document.addEventListener('click', (e) => {
      // Banner buttons
      if (e.target.closest('#cookie-accept')) {
        this.setConsent({ necessary: true, marketing: true });
      }
      if (e.target.closest('#cookie-reject')) {
        this.setConsent({ necessary: true, marketing: false });
      }
      if (e.target.closest('#cookie-configure')) {
        this.showConfigModal();
      }
      
      // Re-open from policy page or footer
      if (e.target.closest('.pdcc-open-modal') || e.target.closest('.js-open-cookies')) {
        e.preventDefault();
        // Abrir directamente el modal de configuración para mejor experiencia de usuario
        this.showConfigModal();
      }
    });
  },

  setConsent(preferences) {
    const data = {
      ...preferences,
      timestamp: new Date().getTime()
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(data));
    this.hideBanner();
    
    if (data.marketing) {
      this.loadElements();
    } else {
      this.blockElements();
    }
  },

  blockElements() {
    // Block Google Maps Iframe
    const maps = document.querySelectorAll('iframe[src*="google.com/maps"]');
    maps.forEach(iframe => {
      if (!iframe.dataset.src) {
        iframe.dataset.src = iframe.src;
        iframe.src = 'about:blank';
        
        // Add a placeholder message
        const wrapper = iframe.parentElement;
        if (wrapper && !wrapper.querySelector('.cookie-placeholder')) {
          const placeholder = document.createElement('div');
          placeholder.className = 'cookie-placeholder absolute inset-0 bg-gray-100 flex flex-col items-center justify-center p-6 text-center z-10';
          placeholder.innerHTML = `
            <svg class="w-10 h-10 text-tinta/20 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 7m0 10V7" />
            </svg>
            <p class="text-sm text-tinta/60 mb-4">Para ver este mapa interactivo debes aceptar las cookies de marketing.</p>
            <button class="js-open-cookies bg-primary text-white px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-accent transition shadow-lg">Configurar Cookies</button>
          `;
          wrapper.style.position = 'relative';
          wrapper.appendChild(placeholder);
        }
      }
    });

    // Block Analytics if needed
  },

  loadElements() {
    // Restore Google Maps
    const maps = document.querySelectorAll('iframe[data-src]');
    maps.forEach(iframe => {
      iframe.src = iframe.dataset.src;
      const placeholder = iframe.parentElement.querySelector('.cookie-placeholder');
      if (placeholder) placeholder.remove();
    });

    // Load dynamic scripts (Analytics)
    this.injectAnalytics();
  },

  injectAnalytics() {
    // GA ID placeholder
    if (!window.ga_injected) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX'; 
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-XXXXXXXXXX');
      window.ga_injected = true;
    }
  }
};
